import React, { type FC, useCallback, useMemo, useState } from 'react';

import { cx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import PlaylistAddedIcon from '@atlaskit/icon-lab/core/playlist-added';
import PlaylistRemoveIcon from '@atlaskit/icon-lab/core/playlist-remove';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { getConfluenceContentAri } from '../../effects/getConfluenceContentAri';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import type { FlagsContainer } from '../audio-player/AudioContainer';

import type { ModifyPlaylistButtons_Playlist$key } from './__generated__/ModifyPlaylistButtons_Playlist.graphql';

export interface baseProps {
	testId?: string;
	flags?: FlagsContainer;
	title?: string;
}

interface playlistMutationArgs {
	cloudId: string;
	contentId: string;
	trackType?: `BRIEFING` | `NARRATION`;
	top?: boolean;
	contentType: string;
}

export interface ModifyPlaylistButtonProps extends baseProps {
	_defaultEngagement_do_not_use?: boolean;
	round?: boolean;
	Flags?: FlagsContainer;
	playlistRef: ModifyPlaylistButtons_Playlist$key | null;
}

const messages = defineMessages({
	addToPlaylistText: {
		id: 'ai-audio.playlist.button.addToPlaylist-label',
		defaultMessage: 'Add to playlist',
		description: 'Label for the chevron up icon to collapse the playlist',
	},
	removeFromPlaylistText: {
		id: 'ai-audio.playlist.button.removeFromPlaylist-label',
		defaultMessage: 'Remove from playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
	inPlaylistText: {
		id: 'ai-audio.playlist.button.inPlaylist-label',
		defaultMessage: 'Added to playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
} as const);

const styles = cssMap({
	container: {
		//typecheck refuses to allow space.400 tokens into height or width
		height: '32px',
		width: '32px',
	},
	round: {
		borderRadius: token('radius.full'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		overflow: 'clip',
	},
});

export const AddToPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	top, //optional to add the new track to the top of the playlist default: false
	flags, //Flags Container
}) => {
	const { formatMessage } = useIntl();
	const { addTrack, isAdding } = useContextMenuActions(flags);

	const addToPlaylist = useCallback(async () => {
		await addTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			[],
			(trackType || 'BRIEFING') as TrackType,
		);
	}, [cloudId, contentId, contentType, trackType, addTrack]);

	return (
		<Tooltip content={formatMessage(messages.addToPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistAddIcon}
					testId={testId && `${testId}-add`}
					label={formatMessage(messages.addToPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
					onClick={addToPlaylist}
					isDisabled={isAdding}
				/>
			)}
		</Tooltip>
	);
};

export const RemoveFromPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	title, //for success flag text
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	flags,
}) => {
	const { formatMessage } = useIntl();
	const { removeTrack, isRemoving } = useContextMenuActions(flags);

	const removeFromPlaylist = useCallback(async () => {
		await removeTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			[],
			(trackType || 'BRIEFING') as TrackType,
			title,
		);
	}, [cloudId, contentId, contentType, trackType, title, removeTrack]);

	return (
		<Tooltip content={formatMessage(messages.removeFromPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistRemoveIcon}
					testId={testId && `${testId}-remove`}
					label={formatMessage(messages.removeFromPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
					onClick={removeFromPlaylist}
					isDisabled={isRemoving}
				/>
			)}
		</Tooltip>
	);
};

//note: when using ModifyPlaylistButton abstraction the tooltip will not be seen because it changes to remove button on hover
export const InPlaylistButton: FC<baseProps> = ({ testId }) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip content={formatMessage(messages.inPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistAddedIcon}
					testId={testId && `${testId}-added`}
					label={formatMessage(messages.inPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
				/>
			)}
		</Tooltip>
	);
};

export const ModifyPlaylistButton: FC<ModifyPlaylistButtonProps & playlistMutationArgs> = ({
	testId, // test ID
	title, //text for remove flag
	round, //applies rounded CSS treatment
	_defaultEngagement_do_not_use = false,
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	playlistRef, //playlist fragment reference
	flags,
}: ModifyPlaylistButtonProps & playlistMutationArgs) => {
	// Type the fragment ref explicitly to satisfy Relay's strict type checking
	const fragmentRef: ModifyPlaylistButtons_Playlist$key | null = playlistRef;

	const playlist = useFragment(
		graphql`
			fragment ModifyPlaylistButtons_Playlist on Query
			@argumentDefinitions(cloudId: { type: "ID!" }) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							mainContent {
								id
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const isInPlaylist = useMemo(() => {
		if (!playlist?.confluence_getPlaylist?.tracks?.nodes) {
			return false;
		}
		const contentIds =
			playlist.confluence_getPlaylist.tracks.nodes
				.map((node) => node?.mainContent?.id)
				.filter(Boolean) || [];
		return contentIds.includes(contentId);
	}, [playlist, contentId]);

	const [isEnagaged, setIsEngaged] = useState(_defaultEngagement_do_not_use);

	return (
		<Box
			// Set the state to true when the mouse enters or the element receives focus
			onMouseEnter={() => setIsEngaged(true)}
			onFocus={() => setIsEngaged(true)}
			// Set the state back to false when the mouse leaves or the element loses focus
			onMouseLeave={() => setIsEngaged(false)}
			onBlur={() => setIsEngaged(false)}
			xcss={cx(styles.container, round && styles.round)}
		>
			{isInPlaylist ? (
				!isEnagaged ? (
					<InPlaylistButton testId={testId} />
				) : (
					<RemoveFromPlaylistButton
						testId={testId}
						cloudId={cloudId}
						contentId={contentId}
						contentType={contentType}
						trackType={trackType}
						flags={flags}
						title={title}
					/>
				)
			) : (
				<AddToPlaylistButton
					testId={testId}
					cloudId={cloudId}
					contentId={contentId}
					contentType={contentType}
					trackType={trackType}
					flags={flags}
				/>
			)}
		</Box>
	);
};
